import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Shield, X } from 'lucide-react';

import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import Table, { type TableColumn } from '../../components/ui/Table';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuthStore } from '../../store/authStore';
import auditService from '../../services/audit.service';
import type { AuditEntry, AuditParams } from '../../types/audit.types';

const PAGE_SIZE = 10;

const AuditPage: React.FC = () => {
  const { user, role, can, isSuperAdmin } = useAuthStore();

  // Dynamic-RBAC: reviewers (records.approve) browse all logs; others see own.
  const isAdmin = isSuperAdmin || role === 'RRMS_SUPER_ADMIN';
  const isCM = !isAdmin && can('records.approve', ['RRMS_CONTENT_MANAGER']);
  const isUser = !isAdmin && !isCM;

  const [filters, setFilters] = useState<AuditParams>({});
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
  const [drilldownTitle, setDrilldownTitle] = useState('');

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [actionInput, setActionInput] = useState('');
  const [userIdInput, setUserIdInput] = useState('');

  const debouncedAction = useDebounce(actionInput, 400);

  useEffect(() => {
    document.title = 'RRMS | Audit Log';
  }, []);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      action: debouncedAction || undefined,
    }));
  }, [debouncedAction]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      from_date: fromDate || undefined,
      to_date: toDate || undefined,
      user_id:
        isUser && user
          ? user.user_id
          : userIdInput
          ? Number(userIdInput)
          : prev.user_id && !userIdInput
          ? undefined
          : prev.user_id,
    }));
  }, [fromDate, toDate, userIdInput, isUser, user]);

  useEffect(() => {
    setPage(1);
  }, [filters.user_id, filters.action, filters.from_date, filters.to_date]);

  const queryParams: AuditParams = useMemo(() => {
    const base: AuditParams = {
      ...filters,
      page,
      limit: PAGE_SIZE,
    };
    if (isUser && user) {
      base.user_id = user.user_id;
    }
    if (isCM && (user as any)?.division_id) {
      (base as any).division_id = (user as any).division_id;
    }
    return base;
  }, [filters, page, isUser, isCM, user]);

  const caseAuditQuery = useQuery({
    queryKey: ['audit-case', selectedCaseId],
    queryFn: () => auditService.getByCase(selectedCaseId!),
    enabled: !!selectedCaseId,
    select: (r) => {
      const d = (r.data as any)?.data ?? r.data;
      return d?.items ?? d?.data ?? d ?? [];
    },
  });

  const auditQuery = useQuery({
    queryKey: ['audit', queryParams, page],
    queryFn: () => auditService.getAll(queryParams),
    enabled: hasSearched,
    select: (r) => {
      const d = (r.data as any)?.data ?? r.data;
      const items: AuditEntry[] = d?.data ?? d?.items ?? [];
      const pagination =
        d?.pagination ?? ({
          page: queryParams.page ?? 1,
          limit: queryParams.limit ?? PAGE_SIZE,
          total: 0,
          totalPages: 0,
        } as const);
      return { items, pagination };
    },
  });

  const handleSearch = () => {
    setHasSearched(true);
    setPage(1);
  };

  const handleClear = () => {
    setFromDate('');
    setToDate('');
    setActionInput('');
    setUserIdInput('');
    setFilters({});
    setHasSearched(false);
    setPage(1);
  };

  const items = auditQuery.data?.items ?? [];
  const pagination =
    auditQuery.data?.pagination ?? {
      page,
      limit: PAGE_SIZE,
      total: items.length,
      totalPages: 1,
    };

  const isLoading = auditQuery.isLoading || auditQuery.isFetching;
  const columns: TableColumn<AuditEntry>[] = [
    {
      key: 'time',
      header: 'Time',
      minWidth: 170,
      tooltip: (row) =>
        row.created_at ? format(new Date(row.created_at), 'dd MMM yyyy, HH:mm') : '—',
      render: (row) =>
        row.created_at ? format(new Date(row.created_at), 'dd MMM yyyy, HH:mm') : '—',
    },
    {
      key: 'user',
      header: 'User',
      minWidth: 150,
      clampLines: 2,
      tooltip: (row) => row.user_name || '—',
      cellStyle: { fontWeight: 600, color: 'var(--color-gray-800)' },
      render: (row) => row.user_name,
    },
    {
      key: 'module',
      header: 'Module',
      minWidth: 140,
      clampLines: 2,
      tooltip: (row) => row.module || '—',
      render: (row) => row.module,
    },
    {
      key: 'action',
      header: 'Action',
      minWidth: 140,
      clampLines: 2,
      tooltip: (row) => row.action || '—',
      render: (row) => row.action,
    },
    {
      key: 'entity',
      header: 'Entity',
      minWidth: 220,
      clampLines: 2,
      tooltip: (row) =>
        row.entity_type
          ? `${row.entity_type.toLowerCase()}: ${row.entity_name ?? row.entity_id ?? '—'}`
          : '—',
      render: (row) =>
        row.entity_type
          ? `${row.entity_type.toLowerCase()}: ${row.entity_name ?? row.entity_id ?? '—'}`
          : '—',
    },
    {
      key: 'outcome',
      header: 'Outcome',
      minWidth: 120,
      tooltip: (row) => row.outcome || '—',
      render: (row) => (
        <Badge color={row.outcome?.toLowerCase() === 'success' ? 'success' : 'error'}>
          {row.outcome}
        </Badge>
      ),
    },
  ];

  return (
    <div className="">
      <PageHeader
        title="Audit Log"
        subtitle="System activity trail"
      />

      <div className="card mb-6 p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold text-[var(--color-gray-700)]">
              From Date
            </span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-md border border-[var(--color-gray-300)] px-3 py-2 text-md"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold text-[var(--color-gray-700)]">
              To Date
            </span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-md border border-[var(--color-gray-300)] px-3 py-2 text-md"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold text-[var(--color-gray-700)]">
              Action
            </span>
            <input
              type="text"
              value={actionInput}
              onChange={(e) => setActionInput(e.target.value)}
              placeholder="e.g. UPLOAD, APPROVE"
              className="rounded-md border border-[var(--color-gray-300)] px-3 py-2 text-md"
            />
          </label>
          {(isCM || isAdmin) && (
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-semibold text-[var(--color-gray-700)]">
                User ID
              </span>
              <input
                type="number"
                value={userIdInput}
                onChange={(e) => setUserIdInput(e.target.value)}
                className="rounded-md border border-[var(--color-gray-300)] px-3 py-2 text-md"
                placeholder="e.g. 123"
              />
            </label>
          )}
        </div>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={handleSearch}
            className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary-light)]"
          >
            Search
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="rounded-md border border-[var(--color-gray-300)] px-4 py-2 text-sm font-medium text-[var(--color-gray-700)] hover:bg-[var(--color-gray-50)]"
          >
            Clear
          </button>
        </div>
      </div>

      {!hasSearched && (
        <div className="card p-10 text-center">
          <Shield
            size={40}
            className="mx-auto mb-4 text-[var(--color-gray-300)]"
          />
          <p className="text-md font-semibold text-[var(--color-gray-700)]">
            Use the filters above to search the audit log
          </p>
          <p className="mt-1 text-sm text-muted">
            Choose a date range, action, or user and click Search.
          </p>
        </div>
      )}

      {hasSearched && (
        <>
          {isLoading && (
            <div className="card p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-12 animate-pulse rounded bg-[var(--color-gray-100)]" style={{ marginBottom: i === 4 ? 0 : 'var(--spacing-3)' }} />
              ))}
            </div>
          )}

          {!isLoading && auditQuery.isError && (
            <div className="card p-8 text-center">
              <p className="text-md text-[var(--color-error)]">
                Failed to load audit log. Please adjust filters or try again.
              </p>
            </div>
          )}

          {!isLoading &&
            !auditQuery.isError &&
            items.length === 0 && (
              <div className="card p-8 text-center">
                <p className="text-md font-semibold text-[var(--color-gray-700)]">
                  No audit entries found for the selected filters.
                </p>
              </div>
            )}

          {!isLoading &&
            !auditQuery.isError &&
            items.length > 0 && (
              <Table
                columns={columns}
                rows={items}
                rowKey={(row) => String(row.id)}
                pagination={pagination}
                onPageChange={setPage}
                minWidth={800}
                onRowClick={(row) => {
                  if (row.entity_id) {
                    setSelectedCaseId(Number(row.entity_id));
                    setDrilldownTitle(`Audit Trail: ${row.entity_name || row.entity_id}`);
                  }
                }}
                rowStyle={(row) => ({
                  cursor: row.entity_id ? 'pointer' : 'default',
                })}
              />
            )}
        </>
      )}

      {selectedCaseId && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: '480px',
          background: 'white', boxShadow: 'var(--shadow-xl)', zIndex: 500,
          display: 'flex', flexDirection: 'column'
        }}>
          <div style={{ padding: 'var(--spacing-4)', borderBottom: '1px solid var(--color-gray-200)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0, fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)' }}>
              {drilldownTitle}
            </h3>
            <button
              type="button"
              onClick={() => setSelectedCaseId(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-500)', padding: '4px' }}
            >
              <X size={18} />
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--spacing-4)' }}>
            {caseAuditQuery.isLoading && (
              <p style={{ color: 'var(--color-gray-500)', fontSize: 'var(--font-size-sm)' }}>Loading...</p>
            )}
            {caseAuditQuery.isError && (
              <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>Failed to load case audit trail.</p>
            )}
            {!caseAuditQuery.isLoading && !caseAuditQuery.isError && (caseAuditQuery.data as any[])?.length === 0 && (
              <p style={{ color: 'var(--color-gray-400)', fontSize: 'var(--font-size-sm)' }}>No audit entries found for this entity.</p>
            )}
            {(caseAuditQuery.data as any[])?.map((entry: any) => (
              <div key={entry.id ?? entry.audit_id} style={{ padding: 'var(--spacing-3)', borderBottom: '1px solid var(--color-gray-100)', fontSize: 'var(--font-size-sm)' }}>
                <p style={{ margin: 0, fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-800)' }}>
                  {entry.action}
                  {entry.outcome && (
                    <span style={{ marginLeft: 8, fontSize: '15px', padding: '1px 6px', borderRadius: '999px',
                      background: entry.outcome?.toLowerCase() === 'success' ? 'var(--color-success-light)' : 'var(--color-error-light)',
                      color: entry.outcome?.toLowerCase() === 'success' ? 'var(--color-success)' : 'var(--color-error)'
                    }}>
                      {entry.outcome}
                    </span>
                  )}
                </p>
                <p style={{ margin: 'var(--spacing-1) 0 0', color: 'var(--color-gray-500)', fontSize: 'var(--font-size-xs)' }}>
                  {entry.user_name} &nbsp;·&nbsp; {entry.created_at ? format(new Date(entry.created_at), 'dd MMM yyyy, HH:mm') : '—'}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditPage;
